import type {
  KollectiveContentTokenLoadOptions,
  KollectiveJitcLoadOptions,
  KollectiveLoadOptions,
  KollectivePlugin,
  KsdkEvent,
  KsdkEventData,
} from '../../../@types/ksdk-api';
import type {
  ConnectionStatusEvent,
  PlaybackStatusChangeEvent,
  SourcesReadyEvent,
} from '../../../@types/ksdk-api/events';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';

import { useLogger } from '../../../contexts/logger-context';
import { loadScript } from '../../../utils/load-script';

const KSDK_SCRIPT_URL = 'https://cdn.kollective.app/sdk/ksdk-latest.min.js';
const KSDK_SCRIPT_NAME = 'mi-kollective-sdk';

export function useKollective(
  enabled: boolean,
  player: VideoJsPlayer,
  setVideoSrc: (videoSrc: videojs.Tech.SourceObject[], force: boolean) => void,
  config?: KollectiveLoadOptions,
  source?: videojs.Tech.SourceObject
) {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'useKollective' }), [mainLogger]);
  const ksdkOptions = useRef<KollectiveLoadOptions | undefined>(undefined); // in case its the same options, we don't load again
  const [ksdkPlugin, setKsdkPlugin] = useState<KollectivePlugin | undefined>(undefined);
  const [sdkLoaded, setSDKLoaded] = useState<boolean>(false);
  const videoSource = useRef<videojs.Tech.SourceObject | undefined>(undefined);
  videoSource.current = source;

  const reloadSource = useCallback(() => {
    if (!enabled) {
      logger.info('Reloading source skipped, as the Kollective SDK is not enabled.');
      return;
    }

    logger.info('Reloading source when true: ', { ksdkPlugin: !!ksdkPlugin, source: !!source });

    if (ksdkPlugin && source) {
      setVideoSrc([source], true);
    }
  }, [enabled, logger, ksdkPlugin, setVideoSrc, source]);

  // Load Kollective SDK, if not already loaded
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // needed for the Kollective SDK to find videojs
    window.videojs = videojs;

    let alive = true;

    const loadKSDK = async () => {
      await loadScript(KSDK_SCRIPT_URL, KSDK_SCRIPT_NAME);
      if (!alive) {
        return;
      }
      setSDKLoaded(true);
    };

    if (window.ksdk) {
      setSDKLoaded(true);
    } else {
      loadKSDK();
    }

    return () => {
      alive = false;
    };
  }, [enabled]);

  // Create Kollective Plugin instance and register event handlers
  useEffect(() => {
    if (!enabled || !player || !sdkLoaded) {
      return;
    }

    const sourcesReadyHandler = (event: unknown) => {
      const { publishedSources } = (event as SourcesReadyEvent).data;
      logger.info('KSDK Plugin :: SourcesReadyHandler - Connected to ECDN. PublishedSource:', publishedSources);

      if (videoSource.current) {
        logger.info('KSDK Plugin :: SourcesReadyHandler - Setting source:', videoSource.current);
        setVideoSrc([videoSource.current], true);
      } else {
        logger.warn(
          'KSDK Plugin :: SourcesReadyHandler - No source available to set. Make sure to set the source after the KSDK is initialized.'
        );
      }
    };

    const errorHandler = (event: unknown) => {
      const { reason, messages } = (event as KsdkEvent<KsdkEventData>).data;

      logger.error(
        `KSDK Plugin :: Error - Reason: ${reason}, Messages: ${messages?.join(', ') || 'No additional messages provided.'}`
      );

      player.error(
        `KSDK Plugin :: Error - Reason: ${reason}, Messages: ${messages?.join(', ') || 'No additional messages provided.'}`
      );
    };

    const infoHandler = (event: unknown) => {
      const { data } = event as KsdkEvent<KsdkEventData>;

      logger.info('KSDK Plugin :: SDK-Information:', data);
    };

    const connectionStatusHandler = (event: unknown) => {
      logger.info(`KSDK Plugin :: Connection Status changed: ${(event as ConnectionStatusEvent).state}`, event);
    };

    const playbackStatusChangeHandler = (event: unknown) => {
      const { data } = event as KsdkEvent<PlaybackStatusChangeEvent>;

      logger.info('KSDK Plugin :: Playback Status changed:', data);
    };

    logger.info('KSDK Plugin :: Creating instance.');
    const ksdkPlugin = window.ksdk.forVideoJS(player, {});
    logger.info('KSDK Plugin :: Instance created.');

    logger.info('KSDK Plugin :: Registering event handlers.');
    ksdkPlugin.addEventListener(ApiEvents.SOURCES_READY, sourcesReadyHandler);
    ksdkPlugin.addEventListener(ApiEvents.ERROR, errorHandler);
    ksdkPlugin.addEventListener(ApiEvents.INFO, infoHandler);
    ksdkPlugin.addEventListener(ApiEvents.CONNECTION_STATUS_EVENT, connectionStatusHandler);
    ksdkPlugin.addEventListener(ApiEvents.PLAYBACK_STATUS_CHANGE, playbackStatusChangeHandler);

    setKsdkPlugin(ksdkPlugin);

    return () => {
      logger.info('KSDK Plugin :: Disposing Kollective SDK instance.');
      ksdkOptions.current = undefined;

      ksdkPlugin.stop();
      logger.info('KSDK Plugin :: Disposed Kollective SDK instance.');
      setKsdkPlugin(undefined);
    };
  }, [enabled, logger, player, sdkLoaded, setVideoSrc]);

  // Connect to Kollective with the provided config, if it changes
  useEffect(() => {
    if (!enabled) {
      return;
    }

    logger.info('Connect-to-ECDN: effect triggered:', { ksdkPlugin: !!ksdkPlugin, config: !!config, source: source });

    if (!ksdkPlugin || !config || !source) {
      return;
    }

    if (isSameOptions(ksdkOptions.current, config)) {
      logger.info('Connect-to-ECDN: Kollective options match the current options. Reusing existing connection.');

      logger.info('Connect-to-ECDN: Set source:', source.src);
      setVideoSrc([source], true);
    } else {
      logger.info('Connect-to-ECDN: Kollective options differ from the current options.', {
        current: ksdkOptions.current,
        next: config,
      });

      ksdkOptions.current = config;

      logger.info('Connect-to-ECDN: Load with options:', ksdkOptions.current, source.src);
      ksdkPlugin
        .load(ksdkOptions.current)
        .then(() => {
          logger.info('Connect-to-ECDN :: Load - Connection to ECDN established.');
        })
        .catch((error) => {
          logger.error('Connect-to-ECDN :: Load - Failed to connect to ECDN:', error);
          player.error('KSDK Plugin :: Load - Failed to connect to ECDN. See console for details.');
        });
    }
  }, [enabled, logger, ksdkPlugin, player, config, source, setVideoSrc]);

  return {
    reloadSource,
  };
}

function isSameOptions(current: KollectiveLoadOptions | undefined, next: KollectiveLoadOptions): boolean {
  if (!current) {
    return false;
  }

  if (isKollectiveJitcOptions(current) && isKollectiveJitcOptions(next)) {
    return (
      current.integratorToken === next.integratorToken &&
      current.jitc === next.jitc &&
      current.playlistType === next.playlistType &&
      current.userEmail === next.userEmail &&
      current.userToken === next.userToken
    );
  } else if (isKollectiveRestOptions(current) && isKollectiveRestOptions(next)) {
    return (
      current.contentToken === next.contentToken &&
      current.playlistType === next.playlistType &&
      current.userEmail === next.userEmail &&
      current.userToken === next.userToken
    );
  }

  return false;
}

// Type Guards
// These are copies from the KSDK API, as we cannot import it directly here
// ksdk-api/interfaces.d.ts

export function isKollectiveRestOptions(options?: KollectiveLoadOptions): options is KollectiveContentTokenLoadOptions {
  if (!options) {
    return false;
  }
  return (options as KollectiveContentTokenLoadOptions).contentToken !== undefined;
}

export function isKollectiveJitcOptions(options?: KollectiveLoadOptions): options is KollectiveJitcLoadOptions {
  if (!options) {
    return false;
  }
  return (
    (options as KollectiveJitcLoadOptions).integratorToken !== undefined &&
    (options as KollectiveJitcLoadOptions).jitc !== undefined
  );
}

// This is a copy from the KSDK API, as we cannot import it directly here
// ksdk-api/events.d.ts
enum ApiEvents {
  BITRATE_RANGE_EVENT = 'BitrateRange',
  CONNECTION_STATUS_EVENT = 'ConnectionStatusEvent',
  ECDN10_PROGRESS = 'LegacyProgress',
  ERROR = 'KsdkError',
  INFO = 'KsdkInfo',
  IP_TYPE = 'NetworkType',
  PLAYBACK_STATUS_CHANGE = 'PlaybackStatusChange',
  SOURCES_READY = 'SourcesReady',
  START_DELIVERY_FINISHED = 'StartDeliveryFinished',
  TRANSFER_SUMMARY = 'TransferSummary',
  WEBSOCKET_CONNECT = 'WebsocketConnect',
}
